import React from 'react';

import { Body } from '@iris/react-components/Body';
import { Card } from '@iris/react-components/Card';

/**
 * @summary Reusable Price Information Card component
 * @description A clickable card that displays pricing-related information with an icon and text content.
 * Used for Fair Market Range, Price Alerts, and Price History functionality.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.icon - Icon or image element to display on the left
 * @param {string} props.title - Bold title text for the card
 * @param {string} props.description - Description text (can include highlighted text)
 * @param {string} props.highlightedText - Text to be highlighted with underline and primary color
 * @param {string} props.dataCmp - Data component identifier for testing
 * @param {React.Ref} ref - Forwarded ref for the button element
 * @returns {React.Component} Clickable card component with pricing information
 */
const PriceInfoCard = React.forwardRef(({
    icon,
    title,
    description,
    highlightedText,
    dataCmp,
    ...props
}, ref) => (
    <button
        className="iris:text-left iris:w-full"
        type="button"
        ref={ref}
        tabIndex={0}
        {...props}
    >
        <Card variant="standard">
            <Card.Content>
                <div className="iris:flex iris:gap-4">
                    {icon}
                    <div>
                        <Body
                            size="sm"
                            as="p"
                            className="iris:font-bold! iris:mb-1!"
                        >
                            {title}
                        </Body>
                        <Body
                            size="sm"
                            as="p"
                            className="iris:mb-0! iris:inline"
                        >
                            {description?.split(highlightedText).map((part, index, array) => (
                                <React.Fragment key={dataCmp + index}>
                                    {part}
                                    {index < array.length - 1 && (
                                        <span className="iris:underline iris:text-[var(--base-color-primary-600)]">
                                            {highlightedText}
                                        </span>
                                    )}
                                </React.Fragment>
                            ))}
                        </Body>
                    </div>
                </div>
            </Card.Content>
        </Card>
    </button>
));

export default PriceInfoCard;
